import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import type { LexicalNode } from "lexical";
import {
  $insertNodes,
  COMMAND_PRIORITY_HIGH,
  COPY_COMMAND,
  PASTE_COMMAND,
  SELECTION_INSERT_CLIPBOARD_NODES_COMMAND,
} from "lexical";
import { sortBy } from "lodash-es";
import type { FC } from "react";
import { useCallback, useEffect } from "react";
import { usePendo } from "../../../analytics/hooks/use-pendo-watch";
import type { ChannelName } from "../../../channels/types";
import { $isDesignHuddleNode } from "../nodes/design-huddle-node";
import { generateNodesFromHtml } from "../utils/generate-nodes-from-html";
import { $insertNode } from "../utils/insert-node";

const sourceFromClipboardEvent = (event: ClipboardEvent): string => {
  // Now we have more logic, make it safe.
  try {
    return sortBy(Array.from(event.clipboardData?.items || []).map(({ type }) => type)).join(", ");
  } catch {
    // Ignore errors
    return "unknown";
  }
};

export const PastePlugin: FC<{
  account_id: string;
  channel: NonNullable<ChannelName>;
  format: "email" | "chat" | "sms";
}> = ({ account_id, channel, format }) => {
  const [editor] = useLexicalComposerContext();
  const pendo = usePendo();
  const crossAccountDesignHuddleNode = useCallback(
    (node: LexicalNode) => {
      // We can't copy a DH project from one account to another, so leave it out
      return !$isDesignHuddleNode(node) || node.__account_id === account_id;
    },
    [account_id],
  );

  useEffect(
    () =>
      editor.registerCommand(
        SELECTION_INSERT_CLIPBOARD_NODES_COMMAND,
        (data) => {
          // Ensure we handle pasting lexical nodes the same way as pasting from outside the editor
          data.nodes.filter(crossAccountDesignHuddleNode).forEach((node) => {
            $insertNode(node);
          });

          return true;
        },
        COMMAND_PRIORITY_HIGH,
      ),
    [editor, crossAccountDesignHuddleNode],
  );

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        event.preventDefault();

        if (!(event instanceof ClipboardEvent) || !event.clipboardData) return false;

        // Let Pendo know the list of item types
        const source = sourceFromClipboardEvent(event);

        pendo?.track("lexical-editor-paste-event", {
          channel,
          source,
        });

        // SMS editors are plain text, so let Lexical's PlainTextPlugin handle the paste.
        if (format === "sms") return false;

        // Handled by ImagePlugin
        if (!event.clipboardData.types.find((type) => type === "text/plain")) return false;

        const html = event.clipboardData.getData("text/html");
        if (!html) return false;

        const nodes = generateNodesFromHtml(editor, html, channel, [crossAccountDesignHuddleNode]);
        $insertNodes(nodes);

        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor, channel, format, crossAccountDesignHuddleNode, pendo]);

  useEffect(
    () =>
      editor.registerCommand(
        COPY_COMMAND,
        (event) => {
          // Let it continue if it's not a clipboard event
          if (!(event instanceof ClipboardEvent)) return true;

          pendo?.track("lexical-editor-copy-event", {
            channel: channel,
          });

          return true;
        },
        COMMAND_PRIORITY_HIGH,
      ),
    [editor, channel, pendo],
  );

  return null;
};
